import { assertNever } from "@redotech/util/type";
import { Call as TelnyxCall } from "@telnyx/webrtc/lib/src/Modules/Verto/webrtc/Call";
import { CustomerJson } from "../../customer";

export enum WebRTCCallState {
  NEW = "new",
  TRYING = "trying",
  REQUESTING = "requesting",
  RECOVERING = "recovering",
  RINGING = "ringing",
  ANSWERING = "answering",
  EARLY = "early",
  ACTIVE = "active",
  HELD = "held",
  HANGUP = "hangup",
  DESTROY = "destroy",
  PURGE = "purge",
}

export enum CallDirection {
  INBOUND = "inbound",
  OUTBOUND = "outbound",
}

export enum RingingStrategy {
  ROUND_ROBIN = "round-robin",
  RING_TEAM = "ring-team",
}

interface DayAvailability {
  enabled?: boolean | null;
  start?: string | null;
  end?: string | null;
}

export enum SupportVoiceCustomAudioType {
  TTS = "tts",
  URL = "url",
}

export interface SupportVoiceCustomAudio {
  enabled: boolean;
  type: SupportVoiceCustomAudioType;
  ttsMessage: string;
  audioUrl: string;
  audioName: string;
}

export interface TeamVoiceConfiguration {
  enabled: boolean;
  ringingStrategy: RingingStrategy;
  sipConnectionId?: string | null;
  sipConnectionCredential?: string | null;
  outboundVoiceProfileId?: string | null;
  voiceApplicationId?: string | null;
  billingGroupId?: string | null;
  playAutomatedMessageWhenAllAgentsAreOffline: boolean;
  sendToVoicemailWhenAllAgentsAreOffline: boolean;
  allowHoldingCallsInQueue: boolean;
  availableHoursEnabled?: boolean | null;
  availableHoursTimezone?: string | null;
  availableHours?: {
    sunday?: DayAvailability | null;
    monday?: DayAvailability | null;
    tuesday?: DayAvailability | null;
    wednesday?: DayAvailability | null;
    thursday?: DayAvailability | null;
    friday?: DayAvailability | null;
    saturday?: DayAvailability | null;
  } | null;
  inboundGreetingMessage: SupportVoiceCustomAudio;
  outboundGreetingMessage: SupportVoiceCustomAudio;
  offHoursMessage: SupportVoiceCustomAudio;
  voicemailMessage: SupportVoiceCustomAudio;
  holdMusicUrl: string;
  holdMusicName: string;
}

export interface CustomerCallData {
  customerId: string;
  customerCallControlId: string;
  customerNumber: string;
}

// ===== moved from redo/web/src/voice/types.ts =====

export enum UserCallAvailability {
  AVAILABLE = "available",
  IN_CALL = "in-call",
  UNAVAILABLE = "unavailable",
}

export enum CallState {
  RINGING = "ringing",
  ACTIVE = "active",
  CONNECTING = "connecting",
  HANGUP = "hangup",
}

export const displayUserCallAvailability = (
  availability: UserCallAvailability,
) => {
  switch (availability) {
    case UserCallAvailability.AVAILABLE:
      return "Available";
    case UserCallAvailability.IN_CALL:
      return "In call";
    case UserCallAvailability.UNAVAILABLE:
      return "Unavailable";
    default:
      assertNever(availability);
  }
};

export type UserAvailabilities = {
  userId: string;
  availability: UserCallAvailability;
}[];

export interface VoiceAgent {
  name: string;
  imageUrl: string | null;
}

export enum SearchByOption {
  STATE_PROVINCE = "stateProvince",
  AREA_CODE = "areaCode",
  CITY_REGION = "cityRegion",
  STARTS_WITH = "startsWith",
  CONTAINS = "contains",
  ENDS_WITH = "endsWith",
}

export type TelnyxPhoneNumberRecord = {
  best_effort: boolean;
  cost_information: {
    currency: string;
    monthly_cost: string;
    upfront_cost: string;
  };
  features: { name: string }[];
  phone_number: string;
  phone_number_type: string;
  quickship: boolean;
  record_type: string;
  region_information: { region_name: string; region_type: string }[];
  reservable: boolean;
  vanity_format: string | null;
};

export interface Call {
  state: CallState;
  callControlId: string;
  direction: CallDirection;
  startTime: Date;
  remoteCallerNumber: string;
  agents: VoiceAgent[];
  customer: CustomerJson;
  telnyxCall?: TelnyxCall;
}
